/**
 * Subject Dashboard Page — Unified entry point for person-centered care.
 *
 * DRR-256: The ONE page care staff opens for all subject-related work.
 * Combines: quick switcher, constellation view, active tasks, care events,
 * clinical summary (FHIR), and briefing panel — all for one selected subject.
 *
 * This is the PRIMARY entry for viewLayer === 'user' (frontline staff).
 * Admin/Architect layers still see the existing DashboardPage.
 *
 * CONSTRAINTS:
 * - DRR-055: Dumb frontend — labels from settingBundle.subjectLabel
 * - DRR-135: Subjects from backend-filtered mySubjects query
 * - DRR-134: FocusContext auto-persists selected subject
 * - DRR-114: T1/T2 data only. No duration variance or staffing ratios.
 * - DRR-123: Clinical tab only appears if FHIR subgraph enabled.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Stack,
  Badge,
  Skeleton,
  Alert,
  Button,
  Divider,
  Paper,
  Box,
  ThemeIcon,
  ScrollArea,
  Tabs,
} from '@mantine/core';
import {
  IconUser,
  IconAlertCircle,
  IconChecklist,
  IconTimeline,
  IconUsers,
  IconStethoscope,
  IconHeartHandshake,
  IconArrowRight,
  IconFocus2,
} from '@tabler/icons-react';

import { useTenantId, useAuth } from '@/auth';
import {
  useMySubjectsQuery,
  useGetTasksBySubjectQuery,
  useGetSettingNavigationQuery,
  useConstellationsByActorQuery,
  GqlTaskStatus,
  GqlTaskPriority,
  GqlActorType,
} from '@/generated/graphql';
import { useFocusContext } from '@/contexts/FocusContext';
import { CareBriefingPanel } from '../people/CareBriefingPanel';
import { SubjectQuickSwitcher, type Subject } from './SubjectQuickSwitcher';
import {
  ConstellationView,
  JourneyTimeline,
  useLiveConstellation,
  useSubjectJourneyTimeline,
  type ConstellationViewNode,
  type ConstellationViewEdge,
} from '@ferp/workflow-forms';
import { FhirClinicalPanel } from '@ferp/subgraph-fhir';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIORITY_COLOR: Record<string, string> = {
  [GqlTaskPriority.CRITICAL]: 'red',
  [GqlTaskPriority.HIGH]: 'orange',
  [GqlTaskPriority.NORMAL]: 'yellow',
  [GqlTaskPriority.LOW]: 'blue',
};

// ============================================================================
// TasksPanel — Active tasks for selected subject
// ============================================================================

interface TasksPanelProps {
  readonly subjectActorId: string;
  readonly tenantId: string;
}

function TasksPanel({ subjectActorId, tenantId }: TasksPanelProps) {
  const { data, loading } = useGetTasksBySubjectQuery({
    variables: { subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const tasks = data?.getTasksBySubject ?? [];

  const { activeTasks, completedTasks, criticalCount, highCount } = useMemo(() => {
    const active = tasks.filter(
      (t) =>
        t.status === GqlTaskStatus.PENDING ||
        t.status === GqlTaskStatus.ASSIGNED ||
        t.status === GqlTaskStatus.IN_PROGRESS,
    );
    const completed = tasks.filter((t) => t.status === GqlTaskStatus.COMPLETED);
    const critical = active.filter((t) => t.priority === GqlTaskPriority.CRITICAL).length;
    const high = active.filter((t) => t.priority === GqlTaskPriority.HIGH).length;
    return { activeTasks: active, completedTasks: completed, criticalCount: critical, highCount: high };
  }, [tasks]);

  if (loading && tasks.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  if (tasks.length === 0) {
    return (
      <Alert icon={<IconChecklist size={16} />} color="blue" variant="light">
        No tasks for this subject.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">Active Tasks</Text>
        <Badge size="sm" color="orange">{activeTasks.length}</Badge>
        {criticalCount > 0 && (
          <Badge size="xs" color="red">{criticalCount} Critical</Badge>
        )}
        {highCount > 0 && (
          <Badge size="xs" color="orange">{highCount} High</Badge>
        )}
      </Group>

      <ScrollArea.Autosize mah={300}>
        <Stack gap="xs">
          {activeTasks.slice(0, 8).map((task) => (
            <Card key={task.id} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={500} size="sm" lineClamp={1}>{task.name}</Text>
                  <Group gap="xs" mt={2}>
                    <Badge
                      size="xs"
                      color={task.status === GqlTaskStatus.IN_PROGRESS ? 'green' : 'yellow'}
                    >
                      {task.status?.replace(/_/g, ' ')}
                    </Badge>
                    {task.priority && (
                      <Badge
                        size="xs"
                        variant="outline"
                        color={PRIORITY_COLOR[task.priority] ?? 'gray'}
                      >
                        {task.priority}
                      </Badge>
                    )}
                  </Group>
                </Box>
                {task.mandateRef && (
                  <Badge size="xs" variant="light" color="violet">
                    Regulated
                  </Badge>
                )}
              </Group>
            </Card>
          ))}
          {activeTasks.length > 8 && (
            <Text size="xs" c="dimmed" ta="center">
              +{activeTasks.length - 8} more tasks
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>

      {completedTasks.length > 0 && (
        <Text size="xs" c="dimmed">
          {completedTasks.length} completed today
        </Text>
      )}
    </Stack>
  );
}

// ============================================================================
// CareTeamPanel — Constellation members for selected subject
// ============================================================================

interface CareTeamPanelProps {
  readonly subjectActorId: string;
}

function CareTeamPanel({ subjectActorId }: CareTeamPanelProps) {
  const { data, loading } = useConstellationsByActorQuery({
    variables: { actorId: subjectActorId },
    skip: !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  const memberships = data?.constellationsByActor?.memberships ?? [];

  if (loading && memberships.length === 0) {
    return <Skeleton height={100} />;
  }

  if (memberships.length === 0) {
    return (
      <Alert icon={<IconUsers size={16} />} color="blue" variant="light">
        No care team assigned yet.
      </Alert>
    );
  }

  // Group by ring
  const ring1 = memberships.filter((m) => m.ring === 1);
  const ring2 = memberships.filter((m) => m.ring === 2);

  return (
    <Stack gap="xs">
      {ring1.length > 0 && (
        <>
          <Text size="xs" fw={600} c="dimmed">Care Team</Text>
          <Group gap="xs">
            {ring1.map((m, i) => (
              <Badge key={i} variant="light" color="blue" leftSection={<IconUser size={10} />}>
                {m.role}
              </Badge>
            ))}
          </Group>
        </>
      )}
      {ring2.length > 0 && (
        <>
          <Text size="xs" fw={600} c="dimmed" mt="xs">Authorities</Text>
          <Group gap="xs">
            {ring2.map((m, i) => (
              <Badge key={i} variant="light" color="violet" leftSection={<IconUser size={10} />}>
                {m.role}
              </Badge>
            ))}
          </Group>
        </>
      )}
    </Stack>
  );
}

// ============================================================================
// EventsPanel — Recent care events timeline
// ============================================================================

interface EventsPanelProps {
  readonly subjectActorId: string;
}

function EventsPanel({ subjectActorId }: EventsPanelProps) {
  const result = useSubjectJourneyTimeline(subjectActorId, 5);

  if (result.loading && result.events.length === 0) {
    return <Skeleton height={100} />;
  }

  if (result.events.length === 0) {
    return (
      <Alert icon={<IconTimeline size={16} />} color="blue" variant="light">
        No care events recorded yet.
      </Alert>
    );
  }

  return (
    <JourneyTimeline
      events={result.events}
      activeEvents={result.activeEvents}
      historicalEvents={result.historicalEvents}
      totalEvents={result.totalEvents}
      activeEventCount={result.activeEventCount}
      loading={result.loading}
    />
  );
}

// ============================================================================
// SubjectDashboardPage
// ============================================================================

export function SubjectDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = useTenantId();
  const { tenant } = useAuth();
  const { focusedSubject, setFocus } = useFocusContext();

  const [briefingOpen, setBriefingOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('tasks');

  // Get subject label from settings
  const { data: settingsData } = useGetSettingNavigationQuery();
  const subjectLabel = settingsData?.settingBundle?.subjectLabel ?? 'Subject';

  // Backend-filtered subjects (DRR-135)
  const { data: subjectsData, loading: subjectsLoading } = useMySubjectsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const subjects: Subject[] = useMemo(() => {
    const raw = subjectsData?.mySubjects ?? [];
    return raw.map((s) => ({
      id: s.id,
      displayName: s.name ?? 'Unknown',
      category: s.category,
      lifecyclePhase: null, // TODO: Derive from workflow instance state
      active: s.active,
    }));
  }, [subjectsData]);

  // Selected subject from URL param or focus context
  const urlSubjectId = searchParams.get('subject');
  const selectedId = urlSubjectId ?? focusedSubject ?? subjects[0]?.id ?? null;

  const selectedSubject = useMemo(
    () => subjects.find((s) => s.id === selectedId),
    [subjects, selectedId],
  );

  // Sync selection to URL and focus context
  const handleSelectSubject = useCallback(
    (id: string) => {
      const subject = subjects.find((s) => s.id === id);
      if (subject) {
        setSearchParams({ subject: id });
        setFocus(id, subject.displayName);
      }
    },
    [subjects, setSearchParams, setFocus],
  );

  // Auto-select first subject if none selected
  useEffect(() => {
    const firstSubject = subjects[0];
    if (!selectedId && firstSubject && !subjectsLoading) {
      handleSelectSubject(firstSubject.id);
    }
  }, [selectedId, subjects, subjectsLoading, handleSelectSubject]);

  // Loading state
  if (subjectsLoading && subjects.length === 0) {
    return (
      <Container size="xl" py="md">
        <Stack gap="md">
          <Skeleton height={40} width={300} />
          <Skeleton height={60} />
          <Grid>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Skeleton height={400} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Skeleton height={400} />
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    );
  }

  // No subjects
  if (subjects.length === 0) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue">
          No {subjectLabel.toLowerCase()}s assigned to you. Contact your supervisor for assignments.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header with quick switcher */}
        <Paper p="sm" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="md" wrap="nowrap">
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconUser size={20} />
              </ThemeIcon>
              <Box>
                <Title order={4}>My {subjectLabel}s</Title>
                <Text size="xs" c="dimmed">
                  {subjects.length} {subjectLabel.toLowerCase()}
                  {subjects.length !== 1 ? 's' : ''} assigned
                </Text>
              </Box>
            </Group>
            <SubjectQuickSwitcher
              subjects={subjects}
              selectedId={selectedId}
              onSelect={handleSelectSubject}
              subjectLabel={subjectLabel}
              loading={subjectsLoading}
            />
          </Group>
        </Paper>

        {/* Selected subject detail */}
        {selectedSubject && (
          <>
            {/* Subject header */}
            <Paper p="md" withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="md">
                  <ThemeIcon size={48} variant="light" color="blue" radius="xl">
                    <IconUser size={24} />
                  </ThemeIcon>
                  <Box>
                    <Title order={3}>{selectedSubject.displayName}</Title>
                    <Group gap="xs" mt={4}>
                      {selectedSubject.category && (
                        <Badge variant="outline">{selectedSubject.category}</Badge>
                      )}
                      {selectedSubject.lifecyclePhase && (
                        <Badge
                          color={
                            selectedSubject.lifecyclePhase === 'active'
                              ? 'green'
                              : selectedSubject.lifecyclePhase === 'onboarding'
                              ? 'blue'
                              : 'gray'
                          }
                        >
                          {selectedSubject.lifecyclePhase}
                        </Badge>
                      )}
                    </Group>
                  </Box>
                </Group>
                <Group gap="xs">
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconHeartHandshake size={16} />}
                    onClick={() => setBriefingOpen(true)}
                  >
                    Brief
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconArrowRight size={16} />}
                    onClick={() => navigate(`/ferp-core/journey?subject=${selectedId}`)}
                  >
                    Full Journey
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    leftSection={<IconChecklist size={16} />}
                    onClick={() => navigate(`/ferp-core/tasks?subject=${selectedId}`)}
                  >
                    All Tasks
                  </Button>
                </Group>
              </Group>
            </Paper>

            {/* Main content grid */}
            <Grid gutter="md">
              {/* Left column: Care team */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Card withBorder h="100%">
                  <Card.Section withBorder inheritPadding py="xs">
                    <Group gap="xs">
                      <IconUsers size={16} />
                      <Text fw={600} size="sm">Care Team</Text>
                    </Group>
                  </Card.Section>
                  <Card.Section inheritPadding py="sm">
                    <CareTeamPanel subjectActorId={selectedId!} />
                  </Card.Section>
                </Card>
              </Grid.Col>

              {/* Right column: Tabs for Tasks, Events, Clinical */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Card withBorder h="100%">
                  <Tabs value={activeTab} onChange={setActiveTab}>
                    <Tabs.List>
                      <Tabs.Tab value="tasks" leftSection={<IconChecklist size={14} />}>
                        Tasks
                      </Tabs.Tab>
                      <Tabs.Tab value="events" leftSection={<IconTimeline size={14} />}>
                        Events
                      </Tabs.Tab>
                      <Tabs.Tab value="clinical" leftSection={<IconStethoscope size={14} />}>
                        Clinical
                      </Tabs.Tab>
                    </Tabs.List>

                    <Box p="sm">
                      <Tabs.Panel value="tasks">
                        <TasksPanel subjectActorId={selectedId!} tenantId={tenantId ?? ''} />
                      </Tabs.Panel>
                      <Tabs.Panel value="events">
                        <EventsPanel subjectActorId={selectedId!} />
                      </Tabs.Panel>
                      <Tabs.Panel value="clinical">
                        {tenant?.num && selectedId ? (
                          <FhirClinicalPanel
                            subjectActorId={selectedId}
                            tenantNum={tenant.num}
                          />
                        ) : (
                          <Alert color="gray" variant="light">
                            Clinical data not available.
                          </Alert>
                        )}
                      </Tabs.Panel>
                    </Box>
                  </Tabs>
                </Card>
              </Grid.Col>
            </Grid>
          </>
        )}

        {/* Care Briefing Panel (Drawer) */}
        {selectedSubject && (
          <CareBriefingPanel
            opened={briefingOpen}
            onClose={() => setBriefingOpen(false)}
            subjectId={selectedSubject.id}
            subjectName={selectedSubject.displayName}
            subjectCategory={selectedSubject.category}
            subjectDescription={null}
            subjectLabel={subjectLabel}
          />
        )}
      </Stack>
    </Container>
  );
}
